import { AudioDevice } from "@/utils/audioSettings";
import { useCallback, useEffect, useRef, useState } from "react";

interface UseAudioLevelMonitorOptions {
  device: AudioDevice | null;
  enabled?: boolean;
}

interface UseAudioLevelMonitorReturn {
  audioLevel: number;
  isActive: boolean;
  hasError: boolean;
}

export const useAudioLevelMonitor = ({
  device,
  enabled = true,
}: UseAudioLevelMonitorOptions): UseAudioLevelMonitorReturn => {
  const [audioLevel, setAudioLevel] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Audio API refs (similar to useAudioRecording)
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Use ref for active state to avoid stale closures (same pattern as useAudioRecording)
  const isActiveRef = useRef<boolean>(false);

  // Real-time audio level analysis (similar to useAudioRecording)
  const updateAudioLevel = useCallback(() => {
    if (!isActiveRef.current || !analyserRef.current || !dataArrayRef.current) {
      return;
    }

    // Get frequency data from the analyser (same as useAudioRecording)
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);

    // Calculate average volume level (same logic as useAudioRecording)
    let sum = 0;
    let maxValue = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      sum += dataArrayRef.current[i];
      maxValue = Math.max(maxValue, dataArrayRef.current[i]);
    }
    const average = sum / dataArrayRef.current.length;

    // Normalize to 0-1 range and apply some smoothing (same as useAudioRecording)
    const normalizedLevel = average / 255;

    // Apply logarithmic scaling for better visual representation (same as useAudioRecording)
    const logLevel =
      normalizedLevel > 0 ? Math.log10(normalizedLevel * 9 + 1) : 0;

    // Ensure minimum level for visual feedback and cap at 1.0 (same sensitivity as useAudioRecording)
    const level = Math.max(0.05, Math.min(1.0, logLevel * 0.8));

    setAudioLevel(level);

    if (isActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  }, []);

  // Start monitoring (similar to useAudioRecording startRecording)
  const startMonitoring = useCallback(async () => {
    if (!device || !enabled) {
      return;
    }

    try {
      console.log("Starting audio level monitoring for:", device.label);
      setHasError(false);

      // Use same audio constraints pattern as useAudioRecording
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: false, // Disable for monitoring to get raw levels
        noiseSuppression: false,
        deviceId: { exact: device.deviceId },
      };

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
      });

      streamRef.current = stream;

      // Set up Web Audio API (same pattern as useAudioRecording)
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Create analyser node (same settings as useAudioRecording but optimized for monitoring)
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; // Same as useAudioRecording
      analyser.smoothingTimeConstant = 0.8; // Same as useAudioRecording
      analyserRef.current = analyser;

      // Create data array for frequency data (same as useAudioRecording)
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(new ArrayBuffer(bufferLength));
      dataArrayRef.current = dataArray;

      // Create source node from the stream and connect to analyser (same as useAudioRecording)
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceNodeRef.current = source;

      // Resume audio context if suspended (same as useAudioRecording)
      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      // Set active state and start monitoring
      isActiveRef.current = true;
      setIsActive(true);
      updateAudioLevel();
    } catch (error) {
      console.error("Failed to start audio monitoring:", error);
      setHasError(true);
      stopMonitoring();
    }
  }, [device, enabled, updateAudioLevel]);

  // Stop monitoring (similar to useAudioRecording cleanup)
  const stopMonitoring = useCallback(() => {
    isActiveRef.current = false;
    setIsActive(false);
    setHasError(false);

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clean up Web Audio API resources (same as useAudioRecording)
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(console.warn);
      }
      audioContextRef.current = null;
    }

    // Stop media stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    // Reset refs
    dataArrayRef.current = null;
    setAudioLevel(0);
  }, []);

  // Setup monitoring when device changes (same pattern as useAudioRecording)
  useEffect(() => {
    if (device && enabled) {
      startMonitoring();
    } else {
      stopMonitoring();
    }

    return stopMonitoring;
  }, [device, enabled, startMonitoring, stopMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return stopMonitoring;
  }, [stopMonitoring]);

  return {
    audioLevel,
    isActive,
    hasError,
  };
};
